import { defineStore } from 'pinia'
import axios from 'axios'
import type { AxiosError } from 'axios'

export type GuestOutcome =
  | 'granted'
  | 'guests_not_allowed'
  | 'ych_staff_must_sign_in'
  | 'session_not_live'
  | 'session_full'
  | 'requirements_not_met'
  | 'login_required'
  | 'access_expired'
  | 'already_signed_in'
  | 'guest_restricted'
  | 'claim_failed'

export interface GuestRegistration {
  firstName: string
  lastName: string
  email: string
  // One of these: the scanned test, or the contest whose judge page was opened
  testCode?: string
  contestId?: number
}

export interface GuestTestLookup {
  guestAllowed: boolean
  sessionLive: boolean
  panelName: string | null
}

type GuestFieldErrors = Partial<Record<keyof GuestRegistration, string>>

interface GuestErrorResponse {
  message?: string
  errors?: Record<string, string[]>
  data?: {
    outcome?: GuestOutcome
    claim_url?: string | null
  }
}

export const useGuestStore = defineStore('guest', {
  state: () => ({
    lookup: null as GuestTestLookup | null,
    lookupLoading: false,
    submitting: false,
    outcome: null as GuestOutcome | null,
    message: null as string | null,
    claimUrl: null as string | null,
    fieldErrors: {} as GuestFieldErrors,
    actionInProgress: false
  }),
  actions: {
    /**
     * Unauthenticated lookup for a scanned test. Describes the panel only
     * when it accepts guests.
     */
    async lookupTest(testCode: string): Promise<GuestTestLookup | null> {
      return this.lookupGuestAccess('/api/guest/test/' + encodeURIComponent(testCode))
    },

    async lookupContest(contestId: number): Promise<GuestTestLookup | null> {
      return this.lookupGuestAccess(`/api/guest/contest/${contestId}`)
    },

    async lookupGuestAccess(url: string): Promise<GuestTestLookup | null> {
      this.lookupLoading = true
      try {
        const response = await axios.get(url)
        const data = response.data.data
        this.lookup = {
          guestAllowed: data.guest_allowed === true,
          sessionLive: data.session_live === true,
          panelName: data.panel_name ?? null
        }
        return this.lookup
      } catch (error) {
        console.error(error)
        this.lookup = null
        return null
      } finally {
        this.lookupLoading = false
      }
    },

    /**
     * Returns true when the guest was signed in.
     */
    async register(registration: GuestRegistration): Promise<boolean> {
      this.submitting = true
      this.fieldErrors = {}
      this.outcome = null
      this.message = null
      this.claimUrl = null
      try {
        const response = await axios.post('/api/guest/register', registration)
        this.outcome = response.data.data.outcome
        return this.outcome === 'granted'
      } catch (error) {
        const response = (error as AxiosError<GuestErrorResponse>).response
        if (response?.status === 422 && response.data.errors) {
          for (const [field, messages] of Object.entries(response.data.errors)) {
            this.fieldErrors[field as keyof GuestRegistration] = messages[0]
          }
        } else {
          this.recordDenial(response?.data, 'We could not start your session. Please try again.')
        }
        if (response?.status !== 422 && response?.status !== 403) {
          console.error(error)
        }
        return false
      } finally {
        this.submitting = false
      }
    },

    /**
     * Stores a denial from the API, including the claim link that comes
     * with an expired guest session.
     */
    recordDenial(data: GuestErrorResponse | undefined, fallbackMessage: string) {
      this.outcome = data?.data?.outcome ?? null
      this.message = data?.message ?? fallbackMessage
      this.claimUrl = data?.data?.claim_url ?? null
    },

    /**
     * "Keep my account": the API publishes the guest to Clerk, ends the guest
     * session and returns the Clerk sign-in URL.
     */
    async keepAccount(): Promise<void> {
      this.actionInProgress = true
      try {
        const response = await axios.post('/api/guest/claim')
        window.location.assign(response.data.data.redirect)
      } catch (error) {
        console.error(error)
        const response = (error as AxiosError<GuestErrorResponse>).response
        this.recordDenial(
          response?.data,
          'We could not set up your account right now. Please try again.'
        )
        this.actionInProgress = false
      }
    },

    /**
     * Follow the signed claim link shown when guest access has ended.
     */
    followClaimLink() {
      if (this.claimUrl) {
        window.location.assign(this.claimUrl)
      }
    },

    /**
     * "Done": end the guest session and reload, so a shared device returns to
     * a clean guest form for the next person.
     */
    async finish(returnPath: string): Promise<void> {
      this.actionInProgress = true
      try {
        await axios.post('/api/guest/logout')
      } catch (error) {
        // Still reload: a failed logout must not leave the next person
        // looking at this guest's screen. The server logs the failure.
        console.error(error)
      }
      window.location.assign(returnPath)
    }
  }
})
